import { useCallback, useEffect, useMemo, useState } from "react";
import adminApi from "../api/adminApi";

function clampInt(value, min, max, fallback) {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, Math.floor(n)));
}

export function useAdminSubscriptions({ adminToken, enabled }) {
  const [query, setQuery] = useState("");
  const [orderBy, setOrderBy] = useState("endsAt");
  const [orderDir, setOrderDir] = useState("ASC");
  const [limit, setLimit] = useState(50);
  const [offset, setOffset] = useState(0);
  const [sourceFilter, setSourceFilter] = useState("all");

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({ active: 0, expiringSoon: 0, bySource: {} });
  const [hub, setHub] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    if (!adminToken) return;
    setLoading(true);
    setError(null);
    try {
      const res = await adminApi.listSubscriptions(adminToken, {
        q: query || "",
        limit,
        offset,
        orderBy,
        orderDir,
        source: sourceFilter === "all" ? "" : sourceFilter
      });
      setItems(Array.isArray(res.data?.items) ? res.data.items : []);
      setTotal(Number(res.data?.total || 0));
      setCounts({
        active: Number(res.data?.counts?.active || 0),
        expiringSoon: Number(res.data?.counts?.expiringSoon || 0),
        bySource: res.data?.counts?.bySource && typeof res.data.counts.bySource === "object"
          ? res.data.counts.bySource
          : {}
      });
      setHub(res.data?.hub && typeof res.data.hub === "object" ? res.data.hub : null);
    } catch (e) {
      console.error("Subscriptions load error:", e);
      setError(e?.response?.data?.error || e?.message || "Ошибка загрузки");
    } finally {
      setLoading(false);
    }
  }, [adminToken, limit, offset, orderBy, orderDir, query, sourceFilter]);

  useEffect(() => {
    if (!enabled) return;
    reload();
  }, [enabled, reload]);

  const page = useMemo(() => Math.floor(offset / limit) + 1, [offset, limit]);
  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / limit)), [total, limit]);

  const setPage = useCallback((nextPage) => {
    const p = clampInt(nextPage, 1, 1_000_000, 1);
    setOffset((p - 1) * limit);
  }, [limit]);

  const toggleOrderDir = useCallback(() => {
    setOrderDir((current) => (current === "ASC" ? "DESC" : "ASC"));
  }, []);

  return {
    state: {
      query,
      orderBy,
      orderDir,
      limit,
      offset,
      sourceFilter,
      items,
      total,
      counts,
      hub,
      loading,
      error,
      page,
      totalPages
    },
    actions: {
      setQuery,
      setOrderBy,
      setOrderDir,
      toggleOrderDir,
      setLimit,
      setOffset,
      setSourceFilter,
      setPage,
      reload
    }
  };
}

export default useAdminSubscriptions;
